import { useState } from "react";
import { Sparkles, X, Loader2, Wand2 } from "lucide-react";
import type { CustomMockup } from "@erc/shared";
import { apiClient } from "../../lib/api";
import env from "../../env";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";

const abs = (u?: string | null) => (!u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${env.API_URL}${u}`);

const THEME_PRESETS = [
  "Christmas", "Halloween", "Autumn / Fall", "Valentine's Day", "Summer beach",
  "Spring floral", "Minimalist neutral", "Boho", "Coastal", "Easter", "Back to school",
];

type ItemStatus = { status: "idle" | "generating" | "done" | "failed"; imageUrl?: string; error?: string };

// Mevcut mockuplardan tema varyanti uret: tisort kilitli, sadece arka plan/tema degisir.
export function MockupThemeModal({ sources, onClose, onCreated }: {
  sources: CustomMockup[]; onClose: () => void; onCreated: () => void;
}) {
  const { models, model, setModel } = useDesignModel("erc:model:theme-mockup");
  const [theme, setTheme] = useState("");
  const [colorNames, setColorNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(sources.map((s) => [s.id, s.color || ""])));
  const [status, setStatus] = useState<Record<string, ItemStatus>>({});
  const [busy, setBusy] = useState(false);
  const [anyCreated, setAnyCreated] = useState(false);

  const canGen = !!theme.trim() && !!model && sources.length > 0 && !busy;

  async function generate() {
    setBusy(true);
    let created = false;
    // Tek tek uret (her biri ~30sn) — ilerleme gorunur, timeout riski dusuk
    for (const s of sources) {
      setStatus((p) => ({ ...p, [s.id]: { status: "generating" } }));
      try {
        const r = await apiClient.generateThemeVariants({
          items: [{ mockupId: s.id, colorName: colorNames[s.id]?.trim() || undefined }],
          theme: theme.trim(), model,
        });
        const res = r.results[0];
        if (res?.newMockupId) {
          created = true;
          setStatus((p) => ({ ...p, [s.id]: { status: "done", imageUrl: res.imageUrl } }));
        } else {
          setStatus((p) => ({ ...p, [s.id]: { status: "failed", error: res?.error || "Üretilemedi" } }));
        }
      } catch (e) {
        setStatus((p) => ({ ...p, [s.id]: { status: "failed", error: e instanceof Error ? e.message : "Hata" } }));
      }
    }
    setBusy(false);
    if (created) { setAnyCreated(true); onCreated(); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Sparkles className="h-5 w-5 text-purple-600" /> Tema Oluştur
            <span className="text-xs font-normal text-gray-400">({sources.length} mockup)</span>
          </h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <p className="text-xs text-gray-500">Tişört birebir korunur; yalnızca arka plan/tema seçtiğin temaya göre yeniden üretilir. Sonuç <b>yeni mockup</b> olarak kaydedilir (tasarım alanları devralınır).</p>

          {/* Tema */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Tema açıklaması</label>
            <textarea value={theme} onChange={(e) => setTheme(e.target.value)} rows={2}
              placeholder="örn. Cozy autumn theme with a few dried leaves, small pumpkins and warm neutral tones"
              className="w-full resize-y rounded-lg border border-gray-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200" />
            <div className="mt-1.5 flex flex-wrap gap-1">
              {THEME_PRESETS.map((t) => (
                <button key={t} onClick={() => setTheme(t)} className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50">{t}</button>
              ))}
            </div>
          </div>

          {/* Model */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600">AI Modeli</label>
            <select value={model} onChange={(e) => setModel(e.target.value)}
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
              {models.length === 0 && <option value="">Model…</option>}
              {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
            </select>
          </div>

          {/* Kaynak mockuplar + renk adi */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Kaynak mockuplar & renk adı</label>
            <div className="space-y-2">
              {sources.map((s) => {
                const st = status[s.id];
                return (
                  <div key={s.id} className="flex items-center gap-2 rounded-lg border border-gray-200 p-2">
                    <img src={abs(s.thumbnailUrl || s.imageUrl)} alt="" className="h-12 w-12 flex-shrink-0 rounded object-cover" />
                    <span className="min-w-0 flex-1 truncate text-xs text-gray-600" title={s.label}>{s.label}</span>
                    <input value={colorNames[s.id] ?? ""} onChange={(e) => setColorNames((p) => ({ ...p, [s.id]: e.target.value }))}
                      placeholder="Renk adı (örn. Sand)"
                      className="w-32 rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-purple-200" />
                    {st?.status === "generating" && <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-purple-500" />}
                    {st?.status === "done" && st.imageUrl && <img src={abs(st.imageUrl)} alt="" className="h-12 w-12 flex-shrink-0 rounded border-2 border-emerald-300 object-cover" />}
                    {st?.status === "failed" && <span className="flex-shrink-0 text-[10px] text-red-500" title={st.error}>Hata</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t p-3">
          {anyCreated && <span className="mr-auto text-xs text-emerald-600">✓ Yeni mockuplar havuza eklendi</span>}
          {!anyCreated && !theme.trim() && <span className="mr-auto text-xs text-amber-600">Tema açıklaması girin</span>}
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Kapat</button>
          <button onClick={generate} disabled={!canGen}
            className="flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Üret ({sources.length})
          </button>
        </div>
      </div>
    </div>
  );
}
